import { useEffect, useRef, useState } from 'react';

const STALL_THRESHOLD_MS = 1500;

interface IAnswerItem {
  type?: string;
  value?: unknown;
  new_agent?: string;
  input?: string;
  [key: string]: any;
}

export type TStallScope = 'text' | 'think';

interface IUseStallByTextOptions {
  /** text：正文区，仅在 progressing 时检测；think：思考区，pending/progressing 均检测 */
  scope?: TStallScope;
}

const isStallWatchActive = (
  streamingState: string | null | undefined,
  scope: TStallScope,
): boolean => {
  if (scope === 'think') {
    return streamingState === 'pending' || streamingState === 'progressing';
  }
  return streamingState === 'progressing';
};

const getTextStreamLength = (items: IAnswerItem[] | undefined): number =>
  (items || []).reduce<number>(
    (sum, item) =>
      item?.type === 'text'
        ? sum + String(item?.value ?? '').length
        : sum,
    0,
  );

/** 思考流实际文本长度（不含子项计数加成） */
const getThinkContentLength = (items: IAnswerItem[] | undefined): number => {
  const list = items || [];
  return list.reduce<number>((sum, item) => {
    if (!item?.type) return sum;
    if (item.type === 'switch_agent') {
      return (
        sum +
        String(item.new_agent ?? '').length +
        String(item.input ?? '').length
      );
    }
    if (
      item.type === 'tool_use' ||
      item.type === 'think_tool' ||
      item.type === 'think' ||
      item.type === 'text' ||
      item.type === 'think_mode'
    ) {
      return sum + String(item.value ?? '').length;
    }
    return sum;
  }, 0);
};

/** 思考流内容长度：新增子项或已有项内容增长均视为活跃 */
const getThinkStreamLength = (items: IAnswerItem[] | undefined): number => {
  const list = items || [];
  const contentLen = getThinkContentLength(list);
  // 新子项到达时即使 value 为空也视为有进展
  return contentLen + list.length * 10000;
};

const hasStreamContent = (
  items: IAnswerItem[] | undefined,
  scope: TStallScope,
): boolean => {
  if (scope === 'think') {
    const list = items || [];
    // pending 初期尚无思考子项时不展示 stall loading
    return list.length > 0;
  }
  return getTextStreamLength(items) > 0;
};

const getStreamLength = (
  items: IAnswerItem[] | undefined,
  scope: TStallScope,
): number =>
  scope === 'think'
    ? getThinkStreamLength(items)
    : getTextStreamLength(items);

/**
 * 文字停顿检测（前端兜底，事件驱动 watchdog）：
 * - 累计长度增长 → 立即翻 stalled=false 并重启 1.5s 定时器
 * - 1.5s 内无增长 → 定时器触发 stalled=true，显示 loading
 * - streamingState 离开监听态 → 清理并 reset
 *
 * 覆盖场景：
 * - text：think→text gap、text→reference gap、网络抖动期的 loading 提示
 * - think：思考/工具调用阶段的停顿 loading（pending + progressing）
 */
export const useStallByText = (
  items: IAnswerItem[] | undefined,
  streamingState: string | null | undefined,
  options?: IUseStallByTextOptions,
): boolean => {
  const scope = options?.scope ?? 'text';
  const [stalled, setStalled] = useState<boolean>(false);
  const prevLenRef = useRef<number>(0);
  const stallTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const clearTimer = (): void => {
      if (stallTimerRef.current) {
        clearTimeout(stallTimerRef.current);
        stallTimerRef.current = null;
      }
    };

    if (!isStallWatchActive(streamingState, scope)) {
      clearTimer();
      setStalled(false);
      prevLenRef.current = 0;
    }

    return clearTimer;
  }, [streamingState, scope]);

  useEffect(() => {
    if (!isStallWatchActive(streamingState, scope)) return;

    const clearTimer = (): void => {
      if (stallTimerRef.current) {
        clearTimeout(stallTimerRef.current);
        stallTimerRef.current = null;
      }
    };

    if (!hasStreamContent(items, scope)) {
      clearTimer();
      setStalled(false);
      prevLenRef.current = 0;
      return;
    }

    const len = getStreamLength(items, scope);

    const startTimer = (): void => {
      if (stallTimerRef.current) clearTimeout(stallTimerRef.current);
      stallTimerRef.current = setTimeout(() => {
        setStalled(true);
      }, STALL_THRESHOLD_MS);
    };

    if (len > prevLenRef.current) {
      prevLenRef.current = len;
      setStalled(false);
      startTimer();
    } else if (!stallTimerRef.current) {
      startTimer();
    }
  }, [items, streamingState, scope]);

  return stalled;
};

export default useStallByText;
